import { useParams } from 'react-router';
import { Fragment } from 'react';
import { Alert, Group, Stack, Modal, Button, Loader, Center, Text } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconMessageCircle, IconMessageCircleOff } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ViewLayout, ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import { CleanTabs } from '@ayocore/platform/client/components/CleanTabs';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { useUrlTabState } from '@ayocore/platform/client/hooks/useUrlTabState';
import { EditLeadForm } from '../forms/EditLeadForm';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import LeadRowsPanel from '../components/LeadRowsPanel';
import { EntityFiles } from '@ayocore/platform/filemanager/client/components';
import { REPOSITORIES } from '@ayocore/platform/common/types/repositories.generated';
import { useLeadViewController } from './useLeadViewController';
import {
  LeadHeader,
  LeadFinancial,
  LeadInfo,
  LeadChannels,
  LeadContactPersons,
  LeadFunnel,
  LeadPartnerDealsTab,
} from './components';
import { LeadViewExtensions } from './LeadViewExtensions';
import { EntityActivityPanel } from '@ayocore/activity/client/components/Activity/ui/embedded/EntityActivityPanel';
import { CRM_ACTIVITY_ENTITY_TYPES } from '@ayocore/activity/common/activity/constants';

interface LeadViewProps {
  leadId?: number;
  isModal?: boolean;
  showEventList?: boolean;
}

/** Дата + время в ru-формате: ДД.ММ.ГГГГ ЧЧ:ММ. */
function formatLeadDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LeadView({
  leadId: propLeadId,
  isModal = false,
  showEventList = true,
}: LeadViewProps) {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useUrlTabState('lead_tab', 'main', { enabled: !isModal });
  const editLeadModal = useModal(EditLeadForm);
  const confirm = useConfirm();
  // Persisted toggle для панели комментариев — должен быть до любых ранних return,
  // иначе React поймёт это как «больше хуков чем в прошлом рендере».
  const [activityPanelOpen, setActivityPanelOpen] = useLocalStorage<boolean>({
    key: 'crm.leadView.activityPanelOpen',
    defaultValue: true,
  });

  if (!id && !propLeadId) throw new Error('Lead ID is not present in url params');

  // Use prop ID if provided, otherwise get from URL params
  const leadId = propLeadId ? propLeadId : parseInt(id as string, 10);

  // Используем хук для получения состояния и экшенов
  const {
    lead,
    partner,
    contactPersons,
    contactChannels,
    loading,
    error,
    permissions,
    modals,
    openDelete,
    closeDelete,
    deleteLead,
    checkCanArchive,
    archiveLead,
    unarchiveLead,
    refreshContacts,
    refreshContactPersons,
    reload,
    updateLeadField,
  } = useLeadViewController(leadId, isModal);

  // Инлайн-модалка удаления (не через useModal): z-index из общего аллокатора,
  // иначе при открытии LeadView в дравере модалка окажется под ним.
  const deleteModalZIndex = useSurfaceZIndex(modals.delete);

  if (loading) {
    return (
      <Center style={{ height: '100%', minHeight: 400 }}>
        <Loader size='lg' />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color='red' title='Ошибка'>
        {error}
      </Alert>
    );
  }

  if (!lead) {
    return (
      <Alert color='yellow' title='Не найден'>
        Лид не найден
      </Alert>
    );
  }

  const handleEditSuccess = () => {
    reload();
  };

  const handleArchive = async () => {
    const check = await checkCanArchive();
    if (!check.allowed) {
      notifications.show({
        color: 'red',
        message: check.message || 'Недостаточно прав для архивирования',
      });
      return;
    }
    if (check.message) {
      const ok = await confirm(check.message, {
        title: 'Архивирование лида',
        confirmLabel: 'Архивировать',
      });
      if (!ok) return;
    }
    const err = await archiveLead();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleUnarchive = async () => {
    const err = await unarchiveLead();
    if (err) notifications.show({ color: 'red', message: err });
  };

  // Расширения от клиентских модулей
  const extensionPayload = { lead, leadTypeSlug: lead.lead_type?.slug ?? null, reload };
  const extraTabs = LeadViewExtensions.tabs.getAll().map(fn => fn(extensionPayload));
  const extraHeaderActions = LeadViewExtensions.headerActions.getAll();
  const extraMenuActions = LeadViewExtensions.menuActions.getAll();
  const extraMainBlocks = LeadViewExtensions.mainPanelBlocks.getAll();
  const shouldHideEventList = LeadViewExtensions.hideEventList
    .getAll()
    .some(fn => fn(extensionPayload));
  // Panel может быть полностью отключена пропсом/extension'ом — тогда кнопку тоже не показываем.
  const activityAllowed = showEventList && !shouldHideEventList;
  const effectiveShowEventList = activityAllowed && activityPanelOpen;

  const mainContent = (
    <>
      <LeadHeader
        lead={lead}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        permissionsLoading={permissions.loading}
        onArchive={!lead.is_archived ? handleArchive : undefined}
        onUnarchive={lead.is_archived ? handleUnarchive : undefined}
        onEdit={() =>
          editLeadModal.open(
            { leadId, onLeadUpdated: handleEditSuccess, title: 'Редактировать лид' },
            { inputSize: 'xs' }
          )
        }
        onDelete={openDelete}
        onTitleSave={v => void updateLeadField('title', v)}
        extraActions={extraHeaderActions.map((fn, i) => (
          <Fragment key={i}>{fn(extensionPayload)}</Fragment>
        ))}
        extraMenuItems={extraMenuActions.map((fn, i) => (
          <Fragment key={`menu-${i}`}>{fn(extensionPayload)}</Fragment>
        ))}
      />

      <CleanTabs value={activeTab} onChange={v => setActiveTab(v ?? 'main')}>
        <CleanTabs.List
          rightSection={
            activityAllowed ? (
              <Button
                size='compact-xs'
                variant='subtle'
                color='blue'
                leftSection={
                  activityPanelOpen ? (
                    <IconMessageCircleOff size={14} />
                  ) : (
                    <IconMessageCircle size={14} />
                  )
                }
                onClick={() => setActivityPanelOpen(prev => !prev)}
              >
                {activityPanelOpen ? 'Скрыть комментарии' : 'Показать комментарии'}
              </Button>
            ) : undefined
          }
        >
          <CleanTabs.Tab value='main'>Основное</CleanTabs.Tab>
          <CleanTabs.Tab value='rows'>Позиции</CleanTabs.Tab>
          {partner?.id && <CleanTabs.Tab value='deals'>Сделки</CleanTabs.Tab>}
          {extraTabs.map(tab => (
            <CleanTabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </CleanTabs.Tab>
          ))}
        </CleanTabs.List>

        <CleanTabs.Panel value='main'>
          <Stack gap='xs'>
            <LeadFunnel lead={lead} onChanged={reload} />

            <LeadFinancial lead={lead} />
            <LeadInfo
              lead={lead}
              partner={partner}
              canEdit={permissions.canEdit && !permissions.loading}
              onPartnerSave={id => void updateLeadField('partner_id', id)}
              onOwnerSave={id => void updateLeadField('manager_id', id)}
              onTagsSave={tags => void updateLeadField('tags', tags)}
              onNotesSave={v => void updateLeadField('notes', v)}
              onDescriptionSave={v => void updateLeadField('description', v)}
              onLeadSourceSave={id => void updateLeadField('lead_source_id', id)}
              onSalesAreaSave={id => void updateLeadField('sales_area_id', id)}
            />

            <LeadChannels
              entityUuid={lead.uuid}
              contactChannels={contactChannels}
              onChannelsChanged={refreshContacts}
            />

            <LeadContactPersons
              entityId={leadId}
              entityType='crm::LeadRepository'
              contactPersons={contactPersons}
              onContactPersonsChanged={refreshContactPersons}
            />

            <ViewPanel>
              <EntityFiles
                entityUuid={lead.uuid}
                entityType={REPOSITORIES.CRM_LEAD}
                directory='leads'
              />
            </ViewPanel>

            {extraMainBlocks.map((fn, i) => (
              <Fragment key={i}>{fn(extensionPayload)}</Fragment>
            ))}

            <ViewPanel>
              <Group gap='lg'>
                <Text size='xs' c='dimmed'>
                  Создан: {formatLeadDateTime(lead.created_at)}
                  {lead.created_by?.name ? ` · ${lead.created_by.name}` : ''}
                </Text>
                <Text size='xs' c='dimmed'>
                  Обновлён: {formatLeadDateTime(lead.updated_at)}
                  {lead.updated_by?.name ? ` · ${lead.updated_by.name}` : ''}
                </Text>
              </Group>
            </ViewPanel>
          </Stack>
        </CleanTabs.Panel>

        <CleanTabs.Panel value='rows'>
          <LeadRowsPanel leadId={leadId} />
        </CleanTabs.Panel>

        {partner?.id && (
          <CleanTabs.Panel value='deals'>
            <LeadPartnerDealsTab leadId={leadId} partnerId={partner.id} />
          </CleanTabs.Panel>
        )}

        {extraTabs.map(tab => (
          <CleanTabs.Panel key={tab.value} value={tab.value}>
            {tab.content}
          </CleanTabs.Panel>
        ))}
      </CleanTabs>
    </>
  );

  return (
    <>
      <ViewLayout storageKey={effectiveShowEventList ? 'crm.leadView.leftPanelPercent' : undefined}>
        <ViewLayout.Stack>{mainContent}</ViewLayout.Stack>
        {effectiveShowEventList && (
          <ViewLayout.Stack>
            <EntityActivityPanel
              entityType={
                partner?.uuid ? CRM_ACTIVITY_ENTITY_TYPES.PARTNER : CRM_ACTIVITY_ENTITY_TYPES.LEAD
              }
              entityUuid={partner?.uuid ?? lead.uuid}
            />
          </ViewLayout.Stack>
        )}
      </ViewLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={modals.delete}
        onClose={closeDelete}
        title='Удалить лид'
        size='sm'
        zIndex={deleteModalZIndex}
      >
        <Stack gap='md'>
          <Text>
            Вы уверены, что хотите удалить лид <strong>"{lead?.title}"</strong>?
          </Text>
          <Text size='sm' c='dimmed'>
            Это действие нельзя отменить.
          </Text>
          <Group justify='flex-end' gap='sm'>
            <Button variant='light' onClick={closeDelete} color='gray' size='xs'>
              Отмена
            </Button>
            <Button variant='filled' onClick={() => void deleteLead()} color='red' size='xs'>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
